import React from "react";
import { motion } from "framer-motion";
import { Container } from "../ui";
import { cn } from "../../utils/cn";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../utils/appLinks";

export interface AppDownloadBannerProps {
  className?: string;
  description?: string;
}

const AppDownloadBanner: React.FC<AppDownloadBannerProps> = ({
  className,
  description = "The app shows the offers around you wherever you are, and lets you verify each redemption with the merchant's OTP at the counter.",
}) => {
  return (
    <section
      id="download"
      className={cn(
        "py-16 sm:py-20 bg-gradient-to-r from-[#6A1FB8] via-[#7B29D3] to-[#8D38EC] relative overflow-hidden text-white",
        className
      )}
    >
      {/* Decorative ambient light */}
      <div className="absolute top-0 right-1/4 w-80 h-80 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="absolute inset-0 bg-line-grid pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none" aria-hidden="true" />

      <Container size="xl" padding="md" className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left Text & CTA */}
          <motion.div
            className="lg:col-span-7 space-y-5 sm:space-y-6 text-center lg:text-left"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.15]">
                Take SetGo with you.
                <br />
                <span className="text-[#FFC727]">Redeem in store, earn instantly.</span>
              </h2>
            </div>

            <p className="text-purple-100/90 text-sm sm:text-base lg:text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
              {description}
            </p>

            {/* App Store / Play Store Badges */}
            <div
              id="download-badges"
              className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 pt-2"
            >
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-badge sheen inline-flex items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white/30 group"
              >
                <svg className="w-6 sm:w-7 h-6 sm:h-7 fill-current" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 0.92-2.87-.91.04-2.02.61-2.67 1.37-.58.67-1.09 1.74-.96 2.78 1.02.08 2.08-.53 2.71-1.28" />
                </svg>
                <div className="text-left leading-tight">
                  <div className="text-[10px] tracking-wider uppercase opacity-75">Download on the</div>
                  <div className="text-xs sm:text-base font-bold">App Store</div>
                </div>
              </a>

              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-badge sheen inline-flex items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white/30 group"
              >
                <svg className="w-5 sm:w-6 h-5 sm:h-6 fill-current" viewBox="0 0 24 24">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186c-.342-.32-.553-.787-.553-1.309V3.123c0-.522.211-.989.552-1.309zm11.238 11.24l2.253 2.254-11.83 6.828 9.577-9.082zm0-2.108L5.27 1.864l11.83 6.828-2.253 2.254zm1.493 1.054l3.056 1.764c.895.517.895 1.365 0 1.882l-3.056 1.764-1.854-1.705 1.854-1.705z" />
                </svg>
                <div className="text-left leading-tight">
                  <div className="text-[10px] tracking-wider uppercase opacity-75">GET IT ON</div>
                  <div className="text-xs sm:text-base font-bold">Google Play</div>
                </div>
              </a>
            </div>
          </motion.div>

          {/* Right Phone Mockup */}
          <motion.div
            className="lg:col-span-5 flex justify-center lg:justify-end"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative max-w-[240px] sm:max-w-[300px] w-full">
              <div className="absolute -inset-8 bg-[radial-gradient(circle,rgba(255,199,39,0.25)_0%,transparent_65%)] blur-2xl pointer-events-none" aria-hidden="true" />
              {/* Outer Phone Frame */}
              <div className="relative rounded-[40px] p-2.5 bg-gradient-to-b from-white/40 via-white/10 to-white/5 shadow-[0_30px_60px_-20px_rgba(20,0,50,0.65),inset_0_1px_0_rgba(255,255,255,0.5)] backdrop-blur-md border border-white/30">
                <div className="rounded-[32px] overflow-hidden bg-[#180A2D] shadow-inner relative">
                  <img
                    src="/Original-customer-app-screen.png"
                    alt="SetGo Mobile App Preview"
                    className="w-full h-auto object-cover rounded-[32px] drop-shadow-md"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export { AppDownloadBanner };
export default AppDownloadBanner;
